// Pure helpers behind the Threads-view channel filter: per-channel counts and
// filtering a thread list by channel.
//
// Both operate over the threads the rail currently holds — the server-capped
// "most recent N" window (the `hasMore` truncation) — so per-channel counts are
// lower bounds, not a user's full history. Fine for an ops filter, but don't
// read them as authoritative totals.

// Canonical chip order, mirroring the `assistant_channels` DB enum. Channels not
// listed here (a future enum value the UI hasn't caught up to) sort last rather
// than vanish.
const CHANNEL_ORDER = ['app', 'sms', 'email', 'headless'] as const;

export interface IChannelCount {
  channel: string;
  count: number;
}

function channelOrderIndex(channel: string): number {
  const i = CHANNEL_ORDER.indexOf(channel as (typeof CHANNEL_ORDER)[number]);
  return i === -1 ? CHANNEL_ORDER.length : i;
}

/**
 * Per-channel counts for the given threads, in canonical order and restricted
 * to channels actually present. Empty in → empty out. Unknown channels sort
 * after the known ones, then alphabetically for stability.
 */
export function deriveChannelCounts<T extends { channel: string }>(threads: readonly T[]): IChannelCount[] {
  const counts = new Map<string, number>();
  for (const thread of threads) {
    counts.set(thread.channel, (counts.get(thread.channel) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([channel, count]) => ({ channel, count }))
    .sort((a, b) => channelOrderIndex(a.channel) - channelOrderIndex(b.channel) || a.channel.localeCompare(b.channel));
}

/**
 * Threads matching the active channel filter. `null` is the "All" sentinel and
 * returns every thread; any other value keeps only rows on that channel.
 */
export function filterThreadsByChannel<T extends { channel: string }>(
  threads: readonly T[],
  channel: string | null
): T[] {
  if (channel === null) {
    return [...threads];
  }
  return threads.filter((thread) => thread.channel === channel);
}
